import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Edit2, Save, X } from 'lucide-react';
import { FormField, Textarea, FormSection, Button } from '../../common/ui';

const RepairDetails = forwardRef(({ 
  repairData, 
  onSave,
  readOnly = false,
  showFinalResolution = true,
  onDraftChange,
  validationErrors = {},
  refreshKey,
  externalEditMode
}, ref) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    final_resolution: repairData.final_resolution || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState('');

  const effectiveEditing = externalEditMode === 'edit' || isEditing;

  // Sync local editing state with external edit mode
  useEffect(() => {
    if (externalEditMode === 'edit' && !isEditing) {
      setEditedData({
        final_resolution: repairData.final_resolution || ''
      });
      setIsEditing(true);
    } else if (externalEditMode !== 'edit' && isEditing) {
      setIsEditing(false);
    }
  }, [externalEditMode]);

  useImperativeHandle(ref, () => ({
    getEditedData: () => editedData,
    hasChanges: () => {
      const orig = {
        final_resolution: (repairData.final_resolution || '').trim()
      };
      const curr = {
        final_resolution: (editedData.final_resolution || '').trim()
      };
      return JSON.stringify(orig) !== JSON.stringify(curr);
    },
    validate: () => {
      const statusesRequiringResolution = ['repaired', 'waiting_for_payment', 'ready_for_shipping', 'completed'];
      const isResolutionRequired = statusesRequiringResolution.includes(repairData.status);
      if (isResolutionRequired && (!editedData.final_resolution || !editedData.final_resolution.trim())) {
        return { valid: false, error: 'Final resolution is required for this status' };
      }
      return { valid: true, error: null };
    },
    reset: () => {
      setEditedData({
        final_resolution: repairData.final_resolution || ''
      });
      setValidationError('');
      setIsEditing(false);
    }
  }));

  // Update internal state when repairData prop changes
  useEffect(() => {
    if (!effectiveEditing) {
      setEditedData({
        final_resolution: repairData.final_resolution || ''
      });
    }
  }, [repairData, effectiveEditing]);

  // Handle forced refresh (e.g. when appending text externally)
  useEffect(() => {
    if (refreshKey > 0) {
      setEditedData(prev => ({
        ...prev,
        final_resolution: repairData.final_resolution || ''
      }));
    }
  }, [refreshKey, repairData.final_resolution]);

  const handleEdit = () => {
    setEditedData({
      final_resolution: repairData.final_resolution || ''
    });
    setValidationError('');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedData({
      final_resolution: repairData.final_resolution || ''
    });
    setValidationError('');
    setIsEditing(false);
  };

  const handleSave = async () => {
    // Check if final_resolution is required based on status
    const statusesRequiringResolution = ['repaired', 'waiting_for_payment', 'ready_for_shipping', 'completed'];
    const isResolutionRequired = statusesRequiringResolution.includes(repairData.status);

    if (isResolutionRequired && (!editedData.final_resolution || !editedData.final_resolution.trim())) {
      setValidationError('Final resolution is required for this status');
      return;
    }

    if (onSave) {
      setIsSaving(true);
      setValidationError('');
      try {
        await onSave(editedData);
        setIsEditing(false);
      } catch (error) {
        console.error('Error saving customer notes:', error);
        setValidationError('Failed to save changes');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newData = {
      ...editedData,
      [name]: value
    };
    
    setEditedData(newData);
    
    // Propagate changes to parent if handler provided
    if (onDraftChange) {
      onDraftChange(newData);
    }
    
    if (validationError) {
      setValidationError('');
    }
  };

  // Check if final_resolution is required based on status (for display)
  const statusesRequiringResolution = ['repaired', 'waiting_for_payment', 'ready_for_shipping', 'completed'];
  const isResolutionRequired = statusesRequiringResolution.includes(repairData.status);

  const renderActions = () => {
    if (readOnly || externalEditMode) return null;

    if (isEditing) {
      return (
        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            variant="primary"
            size="xs"
            icon={Save}
            loading={isSaving}
          >
            Save
          </Button>
          <Button
            onClick={handleCancel}
            variant="ghost"
            size="xs"
            icon={X}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      );
    }

    return (
      <button
        onClick={handleEdit}
        className="text-blue-600 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
        title="Edit customer notes"
      >
        <Edit2 className="h-4 w-4" />
      </button>
    );
  };

  return (
    <FormSection title="Customer visible notes" actions={renderActions()}>
      {validationError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">{validationError}</p>
        </div>
      )}

      <div className="mt-0 space-y-1.5 flex-1 flex flex-col">
        {showFinalResolution && (
          effectiveEditing ? (
            <div className="bg-gray-50/70 rounded p-2 flex-1 flex flex-col">
              <FormField 
                size="sm"
                label={`Final resolution${isResolutionRequired ? ' *' : ''}`}
                error={validationErrors.final_resolution}
                className="flex-1 flex flex-col"
              >
                <Textarea
                  size="sm"
                  name="final_resolution"
                  value={editedData.final_resolution || ''}
                  onChange={handleChange}
                  rows={6}
                  className="flex-1"
                  placeholder="Describe the repair work performed..."
                  disabled={isSaving}
                  required={isResolutionRequired}
                  error={!!validationErrors.final_resolution}
                />
              </FormField>
            </div>
          ) : (
            <FormField 
              size="sm"
              label={`Final resolution${isResolutionRequired ? ' *' : ''}`}
              error={validationErrors.final_resolution}
            >
              <div className="text-sm text-gray-900 whitespace-pre-line border-l-2 border-gray-200 pl-3 min-h-[40px]">
                {repairData.final_resolution || <span className="text-gray-400 italic">No resolution notes provided</span>}
              </div>
            </FormField>
          )
        )}
      </div>
    </FormSection>
  );
});

RepairDetails.displayName = 'RepairDetails';

export default RepairDetails;
